// DashboardPage.tsx
import React, { useState } from 'react';
import { Routes, Route, NavLink, useLocation } from 'react-router-dom';
import {
  Home, Briefcase, Calendar, Lightbulb, Edit3, Image, Users, Send,
  BarChart3, Settings, ChevronDown, Search, LogOut,
  Menu, X, HeartHandshake,
  Building2,
  FolderKanban, Moon, Sun, FileText, Mail, Receipt
} from 'lucide-react';
import DashboardOverview from './DashboardOverview';
import PartnershipsPage from '../partnerships/PartnershipsPage';
import CalendarPage from '../calendar/CalendarPage';
import IdeaVaultPage from '../ideas/IdeaVaultPage';
import { useAuthStore } from '../../store/authStore';
import TeamManagementPage from '../team/teamPage';
import TeamSwitchModal from './TeamSwitchModal';
import { NotificationDropdown } from './NotificationDropdown';
import ContentEditorPage from '../editor/ContentEditorPage';
import AssetLibraryPage from '../assets/AssetLibraryPage';
import ProjectsPage from '../projects/ProjectsPage';
import ContractsPage from '../contracts/ContractsPage';
import SignContractPage from '../contracts/SignContractPage';
import CommunicationHubPage from '../communication/CommunicationHubPage';
import PublishingPage from '../publishing/PublishingPage';
import { ApprovalsLayout } from '../approvals';
import BillingPage from '../billing/BillingPage';
import SettingsPage from '../settings/SettingsPage';


const DashboardPage: React.FC = () => {
  const { user, team, logout } = useAuthStore();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [isTeamSwitchModalOpen, setIsTeamSwitchModalOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Initialize from localStorage or system preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const location = useLocation();

  // Apply theme on mount and when isDarkMode changes
  React.useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  const handleLogout = () => {
    logout();
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };
  const getPlanBadgeColor = (plan?: string): string => {
    switch (plan?.toLowerCase()) {
      case "pro":
        return "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800";
      case "enterprise":
        return "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800";
      case "free":
      default:
        return "bg-primary-100 text-primary-700 border-primary-200 dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-800";
    }
  };



  // Fixed navigation items - removed useResolvedPath issues
  const navItems = [
    {
      path: '',
      label: 'Dashboard',
      icon: <Home className="w-5 h-5" />,
      exact: true
    },
    {
      path: 'ideas',
      label: 'Idea Vault',
      icon: <Lightbulb className="w-5 h-5" />
    },
    {
      path: 'projects',
      label: 'Projects',
      icon: <FolderKanban className="w-5 h-5" />
    },
    {
      path: 'editor',
      label: 'Content Editor',
      icon: <Edit3 className="w-5 h-5" />
    },
    {
      path: 'calendar',
      label: 'Content Calendar',
      icon: <Calendar className="w-5 h-5" />
    },
    {
      path: 'partnerships',
      label: 'Partnerships',
      icon: <Briefcase className="w-5 h-5" />
    },
    {
      path: 'contracts',
      label: 'Contracts & Legal',
      icon: <FileText className="w-5 h-5" />
    },
    {
      path: 'communication',
      label: 'Communication Hub',
      icon: <Mail className="w-5 h-5" />
    },
    {
      path: 'assets',
      label: 'Asset Library',
      icon: <Image className="w-5 h-5" />
    },
    {
      path: 'team',
      label: 'Team Management',
      icon: <Users className="w-5 h-5" />
    },
    {
      path: 'approvals',
      label: 'Approvals & Feedback',
      icon: <HeartHandshake className="w-5 h-5" />,
      submenu: [
        { path: 'approvals/internal', label: 'Internal Feedback' },
        { path: 'approvals/brand', label: 'Brand Approval' }
      ]
    },
    {
      path: 'publishing',
      label: 'Publishing',
      icon: <Send className="w-5 h-5" />
    },
    {
      path: 'billing',
      label: 'Billing & Invoicing',
      icon: <Receipt className="w-5 h-5" />
    },
    {
      path: 'analytics',
      label: 'Analytics',
      icon: <BarChart3 className="w-5 h-5" />,
      submenu: [
        { path: 'analytics/performance', label: 'Performance' },
        { path: 'analytics/roi', label: 'ROI' }
      ]
    },
    {
      path: 'settings',
      label: 'Settings',
      icon: <Settings className="w-5 h-5" />
    }
  ];


  const toggleSubmenu = (label: string) => {
    setActiveSubmenu(activeSubmenu === label ? null : label);
  };

  // Helper function to check if route is active
  const isActiveRoute = (path: string) => {
    if (path === '') {
      return location.pathname === '/dashboard' || location.pathname === '/dashboard/';
    }
    return location.pathname.startsWith(`/dashboard/${path}`);
  };

  return (
    <div className="flex h-[133.33vh] overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Modern Sidebar */}
      <nav className={`
        fixed lg:static inset-y-0 left-0 z-50
        bg-white dark:bg-gray-800 h-[calc(133.33vh-2rem)]
        transition-all duration-300 ease-in-out
        ${isCollapsed ? 'w-20' : 'w-72'}
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col m-4 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700
      `}>
        {/* Enhanced Header */}
        <div className="flex items-center justify-between p-6">
          {!isCollapsed && (
            <div className="flex items-center space-x-1">
              <div className="w-12 h-12 flex items-center justify-center">
                <img src="/logo.png" alt="SubRack Logo" className="w-full h-full object-contain" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  SubRack
                </h1>
              </div>
            </div>
          )}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="hidden lg:flex p-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isCollapsed ? 'rotate-90' : '-rotate-90'}`} />
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="lg:hidden p-2 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Enhanced Navigation */}
        <div className="flex-1 px-4 space-y-2 overflow-y-auto scrollbar-hide">
          {navItems.map((item) => (
            <div key={item.path}>
              <NavLink
                to={item.path}
                end={!!item.exact}
                className={() =>
                  `flex items-center px-4 py-3.5 rounded-2xl transition-all duration-200 group ${isActiveRoute(item.path)
                    ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/30'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white'
                  } ${isCollapsed ? 'justify-center' : 'justify-between'}`
                }
                onClick={(e) => {
                  if (item.submenu) {
                    e.preventDefault();
                    toggleSubmenu(item.label);
                  }
                }}
              >
                <div className="flex items-center space-x-3">
                  <div className={`transition-colors ${isActiveRoute(item.path)
                    ? 'text-white'
                    : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'
                    }`}>
                    {item.icon}
                  </div>
                  {!isCollapsed && <span className="font-medium">{item.label}</span>}
                </div>
                {!isCollapsed && item.submenu && (
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${activeSubmenu === item.label ? 'rotate-180' : ''} ${isActiveRoute(item.path) ? 'text-white/80' : 'text-gray-400'}`}
                  />
                )}
              </NavLink>

              {/* Enhanced Submenu */}
              {!isCollapsed && item.submenu && activeSubmenu === item.label && (
                <div className="ml-4 mt-2 space-y-1">
                  {item.submenu.map((subItem) => (
                    <NavLink
                      key={subItem.path}
                      to={subItem.path}
                      className={({ isActive }) =>
                        `block px-4 py-2.5 rounded-xl text-sm transition-all duration-200 ${isActive
                          ? 'text-primary-600 dark:text-primary-400 font-medium bg-primary-50 dark:bg-primary-900/20'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`
                      }
                    >
                      {subItem.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Enhanced User Section */}
        <div className="p-4 mt-auto border-t border-gray-200 dark:border-gray-700">
          {/* Team Information Section */}
          <div className="mb-4 px-2">
            {!isCollapsed ? (
              <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 p-3 rounded-2xl">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Current Team</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                    {team?.name ?? "No Team"}
                  </p>
                </div>
                <span
                  className={`px-2.5 py-1 rounded-lg text-xs font-bold ${getPlanBadgeColor(
                    team?.plan
                  )}`}
                >
                  {team?.plan
                    ? team.plan.charAt(0).toUpperCase() + team.plan.slice(1)
                    : "Free"}
                </span>
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-2">
                <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
              </div>
            )}
          </div>

          {/* Sidebar Actions */}
          <div className="space-y-1">
            {!isCollapsed ? (
              // Expanded View
              <>
                <button
                  onClick={() => setIsTeamSwitchModalOpen(true)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-2xl transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <Building2 className="w-5 h-5" />
                    <span className="font-medium">Switch Team</span>
                  </div>
                </button>

                <button
                  onClick={toggleTheme}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-2xl transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                    <span className="font-medium">Theme</span>
                  </div>
                </button>

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-2xl transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Logout</span>
                </button>
              </>
            ) : (
              // Collapsed View
              <div className="flex flex-col items-center space-y-2">
                <button
                  onClick={() => setIsTeamSwitchModalOpen(true)}
                  className="p-3 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-xl transition"
                >
                  <Building2 className="w-5 h-5" />
                </button>

                <button
                  onClick={toggleTheme}
                  className="p-3 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-xl transition"
                >
                  {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>

                <button
                  onClick={handleLogout}
                  className="p-3 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        {/* Enhanced Header */}
        <header className="mx-6 mt-4 mb-2 bg-white dark:bg-gray-800 rounded-2xl shadow-[0_0_10px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 flex-1">
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="lg:hidden p-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <Menu className="w-6 h-6 text-gray-600 dark:text-gray-300" />
              </button>

              {/* Search in center */}
              <div className="relative flex-1 max-w-xl">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  placeholder="Search orders, products, or customers..."
                  className="w-full pl-12 pr-4 py-3.5 bg-gray-50 dark:bg-gray-700 border-none rounded-2xl shadow-sm focus:ring-2 focus:ring-primary-500/20 text-gray-600 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                  <kbd className="hidden sm:inline-block px-2 py-1 bg-white dark:bg-gray-600 text-gray-500 dark:text-gray-300 text-xs rounded-lg font-medium shadow-sm">⌘ K</kbd>
                </div>
              </div>
            </div>

            {/* User details and notifications on right */}
            <div className="flex items-center space-x-6 pl-6">
              <div className="flex items-center space-x-2">
                <button className="p-3 bg-gray-50 dark:bg-gray-700 rounded-full text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:shadow-md transition-all duration-200 shadow-sm">
                  <Settings className="w-5 h-5" />
                </button>
                <NotificationDropdown />
              </div>

              {/* User Profile */}
              <div className="flex items-center space-x-3 pl-4 border-l border-gray-200 dark:border-gray-600">
                <div className="text-right hidden md:block">
                  <p className="text-sm font-bold text-gray-900 dark:text-white">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">{user?.role}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-white dark:bg-gray-700 p-1 shadow-sm">
                  <div className="w-full h-full rounded-full bg-gradient-to-br from-primary-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                    {user?.firstName?.[0]}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto bg-transparent">
          <Routes>
            {/* Fixed route paths - removed leading slashes and incorrect paths */}
            <Route path="/" element={<DashboardOverview />} />
            <Route path="partnerships" element={<PartnershipsPage />} />
            <Route path="contracts" element={<ContractsPage />} />
            <Route path="contracts/:id/sign" element={<SignContractPage />} />
            <Route path="communication" element={<CommunicationHubPage />} />
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="ideas" element={<IdeaVaultPage />} />
            <Route path="editor" element={<ContentEditorPage />} />
            <Route path="assets" element={<AssetLibraryPage />} />
            <Route path="team" element={<TeamManagementPage />} />
            <Route path="approvals/*" element={<ApprovalsLayout />} />
            <Route path="publishing/*" element={<PublishingPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="analytics/*" element={
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-red-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Analytics</h3>
                <p className="text-gray-600 mb-6">Track performance and ROI of your content campaigns</p>
                <button className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors font-medium">
                  Coming Soon
                </button>
              </div>
            } />
            <Route path="settings" element={<SettingsPage />} />
          </Routes>
        </main>
      </div>

      {/* Team Switch Modal - ALWAYS RENDERED */}
      < TeamSwitchModal
        isOpen={isTeamSwitchModalOpen}
        onClose={() => {
          setIsTeamSwitchModalOpen(false);
        }}
      />
    </div >
  );
};

export default DashboardPage;
